import React from 'react';
import { Link } from 'react-router-dom';
import { Banknote, CreditCard, DollarSign, Loader2, Printer, Settings, Smartphone, Ticket, Wallet, X } from 'lucide-react';
import QRCode from 'react-qr-code';
import { fmt } from './posV2Utils';
import PayPalPayLater from '../../components/PayPalPayLater';

const PAYMENT_MODES = [
  { mode: 'Espèces', icon: Banknote, label: 'Espèces' },
  { mode: 'CB', icon: CreditCard, label: 'CB' },
  { mode: 'SumUp', icon: Smartphone, label: 'SumUp' },
  { mode: 'PayPal', icon: Wallet, label: 'PayPal 4×' },
  { mode: 'Mixte', icon: DollarSign, label: 'Mixte' },
  { mode: 'Avoir', icon: Ticket, label: 'Avoir' },
];

const PosV2CheckoutPanel = ({
  clientNom,
  onClientChange,
  clientTel,
  clientLoyalty,
  onOpenClient,
  modePaiement,
  onModeChange,
  paiementEspeces,
  onEspecesChange,
  paiementCb,
  onCbChange,
  subtotal,
  remiseType,
  onRemiseTypeChange,
  remiseValue,
  onRemiseValueChange,
  remiseAmount,
  remiseMaxHint = false,
  carlosUser = false,
  total,
  loading,
  error,
  lastSale,
  isOnline,
  onSubmit,
  onPrint,
  printing,
  sumupPending,
  sumupStatus,
  onCancelSumup,
  paypalPending,
  paypalStatus,
  paypalBusy = false,
  onCapturePaypal,
  onCancelPaypal,
  avoirRef,
  onAvoirRefChange,
  onLookupAvoir,
  avoirLookup,
  avoirLoading,
  avoirSuggestions,
  onSelectAvoir,
  paiementAvoir,
  onPaiementAvoirChange,
  complementMode,
  onComplementModeChange,
}) => {
  const sumupDisabled = !isOnline;

  return (
    <section className="rounded-xl border border-white/10 bg-[#151820] p-3 flex flex-col">
      <h2 className="font-bold text-sm mb-3 text-white">Encaissement</h2>
      <div className="flex gap-2 mb-2">
        <input
          className="flex-1 px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-sm min-h-[44px] text-white placeholder:text-gray-500"
          value={clientNom}
          onChange={(e) => onClientChange(e.target.value)}
          placeholder="Nom client"
        />
        {onOpenClient && (
          <button
            type="button"
            onClick={onOpenClient}
            className="shrink-0 px-3 rounded-lg bg-white/10 hover:bg-white/15 text-gray-200 text-xs font-semibold"
            title="Centre Client"
          >
            Client
          </button>
        )}
      </div>
      {clientTel && (
        <p className="text-[10px] text-gray-400 mb-1 font-mono">{clientTel}</p>
      )}
      {clientLoyalty && (
        <p className="text-[10px] text-gray-300 mb-2">
          Fidélité · {clientLoyalty.level_name || '—'} · {clientLoyalty.current_points ?? 0} pts
          {clientLoyalty.pending_reward ? ' · récompense dispo' : ''}
        </p>
      )}
      {!isOnline && (
        <p className="text-[10px] text-amber-300 mb-2">Hors ligne — SumUp / Mixte / Avoir désactivés</p>
      )}
      <Link
        to="/admin/sumup-terminal"
        className="text-[10px] text-gray-400 hover:text-white mb-2 inline-flex items-center gap-1 underline"
      >
        <Settings size={12} />
        Configurer TPE SumUp
      </Link>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {PAYMENT_MODES.map(({ mode, icon: Icon, label }) => {
          const disabled = (sumupDisabled && (mode === 'SumUp' || mode === 'Mixte'))
            || (!isOnline && mode === 'Avoir');
          return (
            <button
              key={mode}
              type="button"
              disabled={disabled || !!sumupPending}
              onClick={() => !disabled && onModeChange(mode)}
              className={`flex flex-col items-center justify-center gap-1 min-h-[56px] rounded-lg text-[10px] font-semibold ${
                modePaiement === mode ? 'bg-red-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/15'
              } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              <Icon size={18} />
              {label}
            </button>
          );
        })}
      </div>
      {modePaiement === 'Mixte' && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Espèces €"
            className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white"
            value={paiementEspeces}
            onChange={(e) => onEspecesChange(e.target.value)}
          />
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="SumUp €"
            className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white"
            value={paiementCb}
            onChange={(e) => onCbChange(e.target.value)}
          />
        </div>
      )}
      {modePaiement === 'Avoir' && (
        <div className="mb-3 space-y-2 p-3 rounded-lg border border-white/10 bg-black/30">
          <label className="block text-[10px] font-semibold text-gray-400 uppercase">Réf. avoir ou nom client</label>
          <div className="relative">
            <div className="flex gap-2">
              <input
                value={avoirRef || ''}
                onChange={(e) => onAvoirRefChange?.(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') onLookupAvoir?.(); }}
                placeholder="AVOIR-… ou nom du client"
                className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white"
                data-testid="pos-v2-avoir-ref"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={onLookupAvoir}
                disabled={avoirLoading}
                className="px-3 rounded-lg bg-white/10 text-xs font-semibold text-gray-200"
              >
                {avoirLoading ? '…' : 'OK'}
              </button>
            </div>
            {!avoirLookup && (avoirSuggestions || []).length > 0 && (
              <ul
                className="absolute z-30 left-0 right-12 mt-1 rounded-xl border max-h-48 overflow-y-auto shadow-2xl"
                style={{ background: '#151820', borderColor: 'rgba(255,255,255,0.12)' }}
                data-testid="pos-v2-avoir-suggestions"
              >
                {avoirSuggestions.map((a) => (
                  <li key={a.id || a.ref}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-white/5"
                      onClick={() => onSelectAvoir?.(a)}
                    >
                      <span className="font-mono font-semibold text-white">{a.ref}</span>
                      <span className="text-emerald-400 ml-2">{fmt(a.solde)} €</span>
                      <span className="block text-[11px] text-gray-400">
                        {a.client_nom || '—'}{a.client_tel ? ` · ${a.client_tel}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {avoirLookup && (
            <div className="text-[11px] text-emerald-300 space-y-1">
              <p>Solde : {fmt(avoirLookup.solde)} € · {avoirLookup.client_nom || '—'}</p>
              <p className="text-gray-500 font-mono">{avoirLookup.source_ref}</p>
              <label className="block text-[10px] text-gray-400">Montant à appliquer</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={avoirLookup.solde}
                value={paiementAvoir}
                onChange={(e) => onPaiementAvoirChange?.(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white"
              />
              {Number(paiementAvoir || 0) < total && (
                <div className="pt-1 space-y-1">
                  <p className="text-amber-300">Reste {fmt(Math.max(0, total - Number(paiementAvoir || 0)))} € — complément</p>
                  <div className="grid grid-cols-3 gap-1">
                    {['Espèces', 'CB', 'SumUp'].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => onComplementModeChange?.(m)}
                        className={`py-2 rounded-lg text-[10px] font-semibold ${complementMode === m ? 'bg-red-600 text-white' : 'bg-white/10 text-gray-300'}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                  {complementMode === 'Espèces' && (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Espèces €"
                      className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white"
                      value={paiementEspeces}
                      onChange={(e) => onEspecesChange(e.target.value)}
                    />
                  )}
                  {(complementMode === 'CB' || complementMode === 'SumUp') && (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={`${complementMode} €`}
                      className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white"
                      value={paiementCb}
                      onChange={(e) => onCbChange(e.target.value)}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {sumupPending && (
        <div className="mb-3 p-3 rounded-lg border border-white/10 bg-black/40 space-y-2 text-gray-200">
          <div className="flex items-center gap-2 text-sm">
            <Smartphone size={16} />
            {sumupPending.terminal ? 'Terminal SumUp SOLO' : 'SumUp'} · {fmt(sumupPending.amount)} €
          </div>
          {sumupPending.terminal && (
            <p className="text-[11px] text-emerald-300">
              Montant envoyé au terminal — regardez l&apos;écran du SOLO « MON TPE » et présentez la carte.
            </p>
          )}
          {sumupPending.terminal && sumupStatus === 'PENDING' && (
            <p className="text-[10px] text-gray-400">
              Le TPE doit afficher « Prêt » avec connexion API (Réglages → Connexions → API).
            </p>
          )}
          <div className="text-[10px] text-gray-400 font-mono truncate">{sumupPending.checkout_id}</div>
          <div className="text-xs text-amber-300">
            Statut : {sumupStatus || 'PENDING'}
            {sumupStatus === 'PAID_PENDING' && ' · Cliquez « VALIDER ENCAISSEMENT »'}
          </div>
          {!sumupPending.terminal && sumupPending.hosted_url && (
            <a
              href={sumupPending.hosted_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-blue-300 underline break-all"
            >
              Ouvrir checkout SumUp
            </a>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onCancelSumup} className="text-xs px-3 py-2 rounded-lg bg-white/10 text-gray-200 flex items-center gap-1">
              <X size={12} /> Annuler
            </button>
          </div>
        </div>
      )}
      <div className="mb-3 space-y-2">
        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
          Remise{remiseMaxHint ? ' (max 20 %)' : ''}
        </label>
        <div className="flex gap-2">
          <select
            value={remiseType}
            onChange={(e) => onRemiseTypeChange(e.target.value)}
            className="px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white min-h-[44px]"
            data-testid="pos-v2-remise-type"
          >
            <option value="pct">%</option>
            <option value="montant">€</option>
          </select>
          <input
            type="number"
            min="0"
            max={remiseMaxHint && remiseType === 'pct' ? 20 : undefined}
            step={remiseType === 'pct' ? '1' : '0.01'}
            value={remiseValue}
            onChange={(e) => onRemiseValueChange(parseFloat(e.target.value) || 0)}
            className="flex-1 px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white min-h-[44px]"
            data-testid="pos-v2-remise-input"
          />
        </div>
        {remiseMaxHint && (
          <p className="text-[10px] text-amber-300/90">Remise limitée à 20 %. Au-delà, validation de la direction requise.</p>
        )}
      </div>
      <div className="space-y-1 mb-3 text-sm">
        <div className="flex justify-between text-gray-400">
          <span>Sous-total</span>
          <span>{fmt(subtotal)} €</span>
        </div>
        {remiseAmount > 0 && (
          <div className="flex justify-between text-emerald-400">
            <span>Remise</span>
            <span>-{fmt(remiseAmount)} €</span>
          </div>
        )}
      </div>
      <div className="flex justify-between text-2xl font-bold mb-3 mt-auto text-white">
        <span>TOTAL</span>
        <span className="text-red-400">{fmt(total)} €</span>
      </div>
      {/* PayPal — paiement en 4× (messaging, montant = total du panier). Informatif : n'encaisse pas. */}
      {total > 0 && (
        <div
          className="mb-3 rounded-lg px-3 py-2"
          style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.08)' }}
          data-testid="pos-paypal-x4"
        >
          <PayPalPayLater amount={total} placement="payment" textColor="black" />
        </div>
      )}
      {/* PayPal — flux d'encaissement réel : QR/lien d'approbation -> capture -> validation. */}
      {modePaiement === 'PayPal' && paypalPending && (
        <div className="mb-3 rounded-lg border border-white/15 bg-black/30 p-3 space-y-2" data-testid="pos-paypal-flow">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-white">Paiement PayPal — {fmt(paypalPending.amount || total)} €</span>
            <button type="button" onClick={onCancelPaypal} className="text-xs text-red-300 hover:text-red-200">Annuler</button>
          </div>
          {paypalStatus !== 'COMPLETED' ? (
            <>
              <p className="text-xs text-gray-400">Le client scanne le QR (ou ouvre le lien) et approuve — en choisissant « Payer en 4× » s'il est éligible.</p>
              {paypalPending.approve_url && (
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-lg flex-shrink-0">
                    <QRCode value={paypalPending.approve_url} size={112} />
                  </div>
                  <div className="flex-1 space-y-2 min-w-0">
                    <a
                      href={paypalPending.approve_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center text-xs font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/15 rounded-lg px-3 py-2"
                      data-testid="pos-paypal-approve-link"
                    >
                      Ouvrir la page PayPal
                    </a>
                    <div className="text-xs text-gray-400">
                      Statut : {paypalStatus === 'APPROVED' ? 'Approuvé ✓ — capturez le paiement' : 'En attente d’approbation du client…'}
                    </div>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={onCapturePaypal}
                disabled={paypalStatus !== 'APPROVED' || paypalBusy}
                className="w-full min-h-[44px] rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-sm font-bold text-white flex items-center justify-center gap-2"
                data-testid="pos-paypal-capture"
              >
                {paypalBusy ? <Loader2 className="animate-spin" size={18} /> : 'Capturer le paiement'}
              </button>
            </>
          ) : (
            <p className="text-sm font-semibold text-emerald-400">Paiement PayPal reçu ✓ — cliquez « VALIDER ENCAISSEMENT ».</p>
          )}
        </div>
      )}
      {error && (
        <pre className="text-[10px] text-red-300 bg-red-950/40 border border-red-500/30 p-2 rounded mb-2 overflow-x-auto whitespace-pre-wrap">{error}</pre>
      )}
          {lastSale && (
        <div className="text-xs text-green-300 bg-green-950/40 border border-green-500/30 p-2 rounded mb-2 space-y-2">
          <div>
            OK · {lastSale.ref_vente} · {lastSale.magasin_id}
            {lastSale.cross_magasin ? ' · stock multi-mag' : ''} · {fmt(lastSale.total)} €
            {lastSale.__offline ? ' · file offline' : ''}
            {lastSale.pending_carlos_hors_stock ? ' · hors-stock en attente Carlos' : ''}
          </div>
          {!lastSale.__offline && (
            <button
              type="button"
              onClick={onPrint}
              disabled={printing}
              className="w-full min-h-[44px] rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-sm font-semibold text-white flex items-center justify-center gap-2"
              data-testid="pos-v2-print-ticket-inline"
            >
              <Printer size={16} />
              {printing ? 'Impression…' : 'Réimprimer le ticket'}
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onSubmit}
        disabled={loading
          || (sumupPending && sumupStatus !== 'PAID_PENDING' && (modePaiement === 'SumUp' || modePaiement === 'Mixte' || (modePaiement === 'Avoir' && complementMode === 'SumUp')))
          || (modePaiement === 'PayPal' && paypalPending && paypalStatus !== 'COMPLETED')}
        className="w-full min-h-[64px] rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-40 font-bold text-lg text-white shadow-lg shadow-red-900/40"
      >
        {loading ? (
          <Loader2 className="animate-spin mx-auto" size={24} />
        ) : modePaiement === 'PayPal' ? (
          paypalPending
            ? (paypalStatus === 'COMPLETED' ? 'VALIDER ENCAISSEMENT' : 'Paiement PayPal en attente…')
            : 'Créer le paiement PayPal'
        ) : sumupPending ? 'VALIDER ENCAISSEMENT' : 'ENCAISSER'}
      </button>
    </section>
  );
};

export default PosV2CheckoutPanel;
